import { c as _c } from "react/compiler-runtime";
/**
 * React hooks for authorization checks.
 *
 * IMPORTANT: These are UI hints only — backend always enforces.
 * Never rely solely on frontend checks for security.
 */

import { useContext } from 'react';
import type { PermissionSnapshot } from '@platform/accesszen-contracts';
import { AccesszenContext } from './AccesszenProvider';
function useAccesszenContext() {
  const context = useContext(AccesszenContext);
  if (!context) {
    throw new Error("useCanAccess must be used within <AccesszenProvider>");
  }
  return context;
}

/**
 * Pure permission check against an already-resolved snapshot — no hook, safe to call
 * from a loop/filter (e.g. filtering a nav-item list) where `useCanAccess` cannot be
 * called per-item. `useCanAccess` below is a thin hook wrapper over this same logic —
 * single source of truth for both call sites.
 */
export function canAccessSnapshot(snapshot: PermissionSnapshot | null, resource: string, action: string, agentName?: string): boolean {
  if (!snapshot) return false;
  if (agentName) {
    const agentPerms = snapshot.agent_permissions[agentName];
    if (!agentPerms) return false;
    return agentPerms.some(p => p.resource === resource && p.action === action && p.allowed);
  }
  return snapshot.org_permissions.some(p => p.resource === resource && p.action === action && p.allowed);
}

/**
 * Check if current user can perform action on resource.
 *
 * For org-level checks, omit agentName:
 *   useCanAccess(PLATFORM_SETTINGS, ADMIN)
 *
 * For agent-level checks, pass the agent name:
 *   useCanAccess(WCA_SITE, READ, AGENT_WORKPLACE_VLM)
 *
 * THIS IS A UI HINT ONLY — backend always re-checks.
 * Returns false while loading or if snapshot is unavailable.
 */
export function useCanAccess(resource, action, agentName) {
  const $ = _c(5);
  const {
    snapshot,
    loading
  } = useAccesszenContext();
  if (loading) {
    return false;
  }
  let t0;
  if ($[0] !== action || $[1] !== agentName || $[2] !== resource || $[3] !== snapshot) {
    t0 = canAccessSnapshot(snapshot, resource, action, agentName);
    $[0] = action;
    $[1] = agentName;
    $[2] = resource;
    $[3] = snapshot;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
}

/**
 * Check if current user has access to a specific workspace.
 *
 * THIS IS A UI HINT ONLY — backend always re-checks.
 * Returns false while loading or if snapshot is unavailable.
 */
export function useHasWorkspaceAccess(workspaceId) {
  const $ = _c(3);
  const {
    snapshot,
    loading
  } = useAccesszenContext();
  if (loading || !snapshot) {
    return false;
  }
  let t0;
  if ($[0] !== snapshot.workspace_access || $[1] !== workspaceId) {
    t0 = snapshot.workspace_access.includes(workspaceId);
    $[0] = snapshot.workspace_access;
    $[1] = workspaceId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/**
 * Get the user's highest role for a specific agent.
 *
 * Returns the role string (e.g. "spend:admin") or null if no role assigned.
 * Returns null while loading or if snapshot is unavailable.
 */
export function useAgentRole(agentName) {
  const {
    snapshot,
    loading
  } = useAccesszenContext();
  if (loading || !snapshot) {
    return null;
  }
  return snapshot.agent_roles[agentName] ?? null;
}

/**
 * Get the full permission snapshot for the current user.
 * Fetched once on mount, refreshed on workspace switch.
 *
 * Returns null while loading.
 */
export function usePermissionSnapshot() {
  const {
    snapshot
  } = useAccesszenContext();
  return snapshot;
}

/**
 * True while the permission snapshot's initial fetch is in flight.
 *
 * Use this to distinguish "still loading" from "loaded, but empty" when a UI
 * derives visibility from `usePermissionSnapshot()` (e.g. `snapshot === null`
 * is ambiguous between those two states on its own).
 */
export function useAccesszenLoading() {
  const {
    loading
  } = useAccesszenContext();
  return loading;
}

/**
 * The provider's silent snapshot re-fetch, for callers that just changed the
 * current user's own grants (e.g. an agent-role replace) so the top-bar agent
 * menu updates without a reload or a loading flash. Returns a no-op outside
 * <AccesszenProvider> — a mutation hook must not crash a tree that renders
 * without one. Ref: ASC-10648
 */
export function useAccesszenRefresh() {
  const context = useContext(AccesszenContext);
  return context?.revalidate ?? noopRefresh;
}
async function noopRefresh(): Promise<void> {}